import React, { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import ToggleSwitch from '../components/common/ToggleSwitch';
import { ACCESS_CONTROLS, SUB_ITEM_SECTIONS } from '../data/accessControls';

// The fixed master list of access-control sub-items. Each item has a description
// and an active/inactive toggle. This is the source list that the Subscription
// page assigns to packages (with values). UI/local-state only — no enforcement.
const SubItems = () => {
  const [enabled, setEnabled] = useState(() =>
    ACCESS_CONTROLS.reduce((acc, c) => ({ ...acc, [c.id]: true }), {})
  );
  const toggle = (id) => setEnabled((prev) => ({ ...prev, [id]: !prev[id] }));

  const activeCount = Object.values(enabled).filter(Boolean).length;

  return (
    <div className="min-h-full w-full">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 sm:mb-8">
        <div className="flex items-center gap-3">
          <span className="p-2.5 rounded-2xl bg-[#4ABDBD]/10 text-[#4ABDBD] shadow-sm">
            <ShieldCheck className="w-6 h-6 sm:w-7 sm:h-7" />
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="sub-items-title">Sub-items</h1>
            <p className="text-sm text-slate-500">The master list of access controls — assign these to packages on the Subscription page.</p>
          </div>
        </div>
        <span className="text-xs font-bold text-[#3AA8A8] bg-white/70 border border-[#4ABDBD]/20 px-3 py-1.5 rounded-full shadow-sm" data-testid="sub-items-count">
          {activeCount}/{ACCESS_CONTROLS.length} active
        </span>
      </div>

      {/* Sections — sub-items grouped by category (Doctor / Patient / AI / Custom) */}
      <div className="space-y-8" data-testid="sub-items-list">
        {SUB_ITEM_SECTIONS.map((section) => {
          const items = ACCESS_CONTROLS.filter((c) => c.section === section.id);
          if (items.length === 0) return null;
          const sectionOn = items.filter((c) => enabled[c.id]).length;
          return (
            <section key={section.id} data-testid={`sub-items-section-${section.id}`}>
              {/* Section header */}
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-lg font-black text-[#1a365d]">{section.label}</h2>
                  <p className="text-xs text-slate-400">{section.description}</p>
                </div>
                <span className="text-[11px] font-bold text-[#3AA8A8] bg-[#4ABDBD]/10 px-2.5 py-1 rounded-full shrink-0">
                  {sectionOn}/{items.length}
                </span>
              </div>

              {/* Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                {items.map((item) => {
                  const on = !!enabled[item.id];
                  return (
                    <div
                      key={item.id}
                      data-testid={`sub-items-row-${item.id}`}
                      className={`group bg-white rounded-2xl border shadow-sm hover:shadow-md transition-all p-4 sm:p-5 flex flex-col gap-2 ${
                        on ? 'border-[#4ABDBD]/30' : 'border-gray-100'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0 flex-wrap">
                          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${on ? 'bg-[#4ABDBD]' : 'bg-gray-300'}`} />
                          <h3 className="text-base font-bold text-[#1a365d]" data-testid={`sub-items-label-${item.id}`}>{item.label}</h3>
                          {item.type === 'value' && (
                            <span className="text-[10px] font-bold uppercase tracking-wide text-[#3AA8A8] bg-[#4ABDBD]/10 px-1.5 py-0.5 rounded">Value</span>
                          )}
                        </div>
                        {item.section === 'static' ? (
                          <span className="text-[10px] font-bold uppercase tracking-wide text-[#3AA8A8] bg-[#4ABDBD]/10 px-2 py-0.5 rounded shrink-0" data-testid={`sub-items-static-${item.id}`}>Always on</span>
                        ) : (
                          <ToggleSwitch id={item.id} on={on} onToggle={() => toggle(item.id)} />
                        )}
                      </div>
                      <p className="text-sm text-slate-500 leading-relaxed">{item.description}</p>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default SubItems;
